import React, { Component } from 'react'
import enhanceWithClickOutside from 'react-click-outside'
import Field from '../Field/Field'
import './SelectField.scss'
import PropTypes from 'prop-types'
import SelectFieldItem from './SelectFieldItem'

class SelectField extends Component {
  state = {
    isOpen: false,
    value: '',
    focusedItem: {},
    options: [],
  }

  list = React.createRef()

  componentDidUpdate = prevProps => {
    const { options, selectedItem } = this.props
    if (options !== prevProps.options) {
      this.setState({ options })
    }
    if (selectedItem !== prevProps.selectedItem) {
      this.setState({
        value: selectedItem.label ? selectedItem.label : selectedItem.key,
      })
    }
  }

  componentDidMount() {
    const { options, selectedItem } = this.props
    this.setState({
      options,
      value: selectedItem.label ? selectedItem.label : selectedItem.key,
    })
  }

  sanitizeValue = value =>
    value
      .toLowerCase()
      .replace(/[()]/g, '')
      .trim()

  filterOptions = value => {
    const { options } = this.props
    const sanitizedValue = this.sanitizeValue(value)
    const hasMatchingRoutesList = options[0].list
    const filterOptionsOnValue = list =>
      list.filter(({ label, key }) => {
        return (
          this.sanitizeValue(label).match(sanitizedValue) ||
          this.sanitizeValue(key).match(sanitizedValue)
        )
      })
    if (hasMatchingRoutesList) {
      this.setState({
        options: options.map(option => {
          return {
            ...option,
            list: filterOptionsOnValue(option.list),
          }
        }),
      })
    } else {
      const matchList = filterOptionsOnValue(options)
      this.setState({
        options: matchList.length ? matchList : options,
      })
    }
  }

  handleType = e => {
    const { value } = e.target
    this.setState({ value })
    this.filterOptions(value)
  }

  setFocusedItem = direction => {
    const { focusedItem, options } = this.state
    const listOfItems =
      options.length === 2 && options[0].title
        ? options.reduce(
            (mergedList, option) => [...mergedList, ...option.list],
            []
          )
        : options
    if (!listOfItems.length) return
    const focusKeys = listOfItems.map(x => x.focusKey)
    if (direction === 'next') {
      const nextItem =
        focusKeys.indexOf(focusedItem.focusKey) + 1 >= listOfItems.length
          ? listOfItems[listOfItems.length - 1]
          : listOfItems[focusKeys.indexOf(focusedItem.focusKey) + 1]
      this.focusItem(nextItem)
    } else {
      const prevItem =
        focusKeys.indexOf(focusedItem.focusKey) - 1 <= 0
          ? listOfItems[0]
          : listOfItems[focusKeys.indexOf(focusedItem.focusKey) - 1]
      this.focusItem(prevItem)
    }
  }

  handleOnKeyDown = event => {
    const { focusedItem } = this.state
    const { key } = event

    switch (key) {
      case 'ArrowDown':
        event.preventDefault()
        this.setFocusedItem('next')
        break
      case 'ArrowUp':
        event.preventDefault()
        this.setFocusedItem('previous')
        break
      case 'Enter':
        if (focusedItem.key) {
          this.selectItem(focusedItem)
        }
        break
      default:
        break
    }
  }

  focusItem = item => {
    this.setState({ focusedItem: item })
    this.scrollIntoView(item.focusKey)
  }

  scrollIntoView = key => {
    const item = document.querySelector(`[data-focus-key="${key}"]`)
    if (!item) return
    const list = this.list.current
    const itemPosition = item.offsetTop
    const itemHeight = item.getBoundingClientRect().height
    const containerHeight = list.getBoundingClientRect().height - itemHeight
    const currentView = list.scrollTop + containerHeight
    if (currentView < itemPosition) {
      list.scrollTop = itemPosition - containerHeight
    } else if (currentView > itemPosition + containerHeight) {
      list.scrollTop = list.scrollTop - itemHeight
    }
  }

  selectItem = item => {
    this.setState({
      isOpen: false,
      focusedItem: {},
      value: item.label,
    })
    this.props.handleChange(item)
  }

  /**
   * Close the list when clicking outside of the component
   * This method is required by enhanceWithClickOutside() module
   */
  handleClickOutside = () => this.closeList()

  openList = () => this.setState({ isOpen: true })

  closeList = () =>
    this.setState({
      isOpen: false,
      focusedItem: {},
    })

  handleOnBlur = event => {
    // IE sets event.relatedTarget as document.activeElement and returns null instead
    const clickedItem = event.relatedTarget || document.activeElement
    if (!clickedItem) return
    const isListItemClicked =
      clickedItem && clickedItem.hasAttribute('data-focused')
    if (!isListItemClicked) this.closeList()
  }

  render() {
    const {
      label,
      placeholder,
      autoComplete,
      errorMessage,
      dataElement,
    } = this.props
    const { value, isOpen, options, focusedItem } = this.state
    return (
      <div className="SelectField" data-element={dataElement}>
        <Field
          label={label}
          placeholder={placeholder}
          value={value}
          active={isOpen}
          icon="down"
          handleClick={this.openList}
          handleOnFocus={this.openList}
          handleOnBlur={this.handleOnBlur}
          autoComplete={autoComplete}
          handleType={this.handleType}
          handleOnKeyDown={this.handleOnKeyDown}
        />
        {isOpen && (
          <section
            ref={this.list}
            className={`SelectField__listWrapper ${
              errorMessage ? 'has-error' : ''
            }`}
          >
            <ul className="SelectField__list" onBlur={this.handleOnBlur}>
              {options.map((item, i) => (
                <React.Fragment key={i}>
                  {item.title && <h6>{item.title}</h6>}
                  {item.list &&
                    item.list.map(item => {
                      return (
                        <SelectFieldItem
                          item={item}
                          isFocused={item.focusKey === focusedItem.focusKey}
                          key={item.key}
                          handleOnClick={this.selectItem}
                        />
                      )
                    })}
                  {!item.list && (
                    <SelectFieldItem
                      item={item}
                      isFocused={item.focusKey === focusedItem.focusKey}
                      key={item.key}
                      handleOnClick={this.selectItem}
                    />
                  )}
                </React.Fragment>
              ))}
            </ul>
          </section>
        )}
        {errorMessage && <span className="error-message">{errorMessage}</span>}
      </div>
    )
  }
}

SelectField.propTypes = {
  dataElement: PropTypes.string,
  handleChange: PropTypes.func,
  label: PropTypes.string,
  selectedItem: PropTypes.object,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  filterLabel: PropTypes.string,
  filteredResult: PropTypes.object,
  autoComplete: PropTypes.bool,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
}

export default enhanceWithClickOutside(SelectField)
